import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { PiWavesBold } from 'react-icons/pi';
import { useSearchParams } from 'react-router-dom';
import {
  useGetAirQualityForecastQuery,
  useGetAirQualityObservationQuery,
} from 'services/weather/weather.api';
import 'features/Weather/assets/weatherDataDisplay.css';

import WeatherCardGrid from '../../components/WeatherCardGrid/WeatherCardGrid';
import AirQualityStationAccordion from './components/AirQualityStationAccordion/AirQualityStationAccordion';
import { airQualityDetails, getAirQualityCategoryColor } from './config/airQuality.config';
import {
  getAirQualityCardKey,
  getAirQualityCardTitle,
  getAirQualityForecastStations,
  getAirQualityMode,
  getAirQualityObservationCards,
} from './utils/airQuality.utils';
import EmptyState from 'components/EmptyState/EmptyState';

const AirQuality = () => {
  const [params] = useSearchParams();
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const airQualityMode = getAirQualityMode(params.get('subtab'));
  const isForecast = airQualityMode === 'forecast';
  const { data: airQualityObservations = [], isLoading: loadingAirQualityObservations } =
    useGetAirQualityObservationQuery(effectiveLocation, {
      skip: isForecast,
    });
  const { data: airQualityForecast = [], isLoading: loadingAirQualityForecast } =
    useGetAirQualityForecastQuery(effectiveLocation, {
      skip: !isForecast,
    });

  const airQualityObservationCards = useMemo(
    () => getAirQualityObservationCards(airQualityObservations),
    [airQualityObservations],
  );

  const airQualityForecastStations = useMemo(
    () => getAirQualityForecastStations(airQualityForecast),
    [airQualityForecast],
  );

  const cardGridProps = {
    cardDetails: airQualityDetails,
    getCardKey: getAirQualityCardKey,
    getCardTitle: getAirQualityCardTitle,
    getCardIcon: (item) => (
      <PiWavesBold size={25} color={getAirQualityCategoryColor(item?.category)} />
    ),
  };

  return (
    <>
      {!isForecast ? (
        <WeatherCardGrid
          loading={loadingAirQualityObservations}
          pageHeading={'Air Quality Observations'}
          forecast={airQualityObservationCards}
          emptyMessage="No air quality observations near you."
          {...cardGridProps}
        />
      ) : (
        <>
          {!loadingAirQualityForecast ? (
            <Container
              fluid
              className="weather-data-display d-flex flex-column h-100 user-select-none gap-3 px-0"
            >
              {airQualityForecastStations.length ? (
                <>
                  <div className="pb-2 border-bottom weather-data-display-heading">
                    <h2 className="m-0 fw-bold lh-sm weather-data-display-title">
                      Air Quality Forecast
                    </h2>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {airQualityForecastStations.map((station, index) => (
                      <AirQualityStationAccordion
                        key={getAirQualityCardKey(station, index)}
                        station={station}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <EmptyState
                  title="Air Quality Forecast"
                  message="No air quality forecast data available."
                />
              )}
            </Container>
          ) : (
            <WidgetLoadingAnimation />
          )}
        </>
      )}
    </>
  );
};

export default AirQuality;
